import { useCallback, useMemo, useState, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';

import { filterListWithQuery } from '@ts-core/utils/filters';

export default function useStudentsSelector({
  selectedStudents: initialSelectedStudents,
  students: allStudents,
  loading,
  submitting,
  submitButtonTitle,
  onConfirmSelection,
  allowEmptySelection = false,
  disabled = false,
}) {
  const [selectedStudentsIds, setSelectedStudentsIds] = useState({});
  const [query, setQuery] = useState();

  const students = useMemo(() => {
    return filterListWithQuery({
      list: allStudents,
      query,
      fields: ['name', 'email'],
    });
  }, [query, allStudents]);

  const isStudentSelected = useCallback(
    (student) => {
      return !!selectedStudentsIds[student._id];
    },
    [selectedStudentsIds]
  );

  const selectStudents = useCallback((studentsIds) => {
    if (!studentsIds) return;
    setSelectedStudentsIds(studentsIds.reduce((result, student) => ({ ...result, [student]: true }), {}));
  }, []);

  useEffect(() => {
    if (!isEmpty(selectedStudentsIds)) {
      selectStudents(initialSelectedStudents);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allStudents, initialSelectedStudents]);

  useEffect(() => {
    if (!isEmpty(initialSelectedStudents)) {
      selectStudents(initialSelectedStudents);
    }
  }, [initialSelectedStudents, selectStudents]);

  const selectedStudents = useMemo(() => {
    return allStudents.filter(isStudentSelected);
  }, [allStudents, isStudentSelected]);

  const allStudentSelected = useMemo(() => {
    if (!students.length) return false;
    return students.filter(isStudentSelected).length === students.length;
  }, [students, isStudentSelected]);

  const noStudentsSelected = useMemo(() => {
    return students.filter(isStudentSelected).length === 0;
  }, [students, isStudentSelected]);

  const onToggleAll = useCallback(() => {
    if (allStudentSelected) {
      setSelectedStudentsIds({});
    } else {
      selectStudents(students.map((s) => s._id));
    }
  }, [allStudentSelected, students, selectStudents]);

  const onToggleStudent = useCallback((student) => {
    setSelectedStudentsIds((data) => ({
      ...data,
      [student._id]: !data[student._id],
    }));
  }, []);

  const onSearchStudent = useCallback((newQuery) => {
    setQuery(newQuery);
  }, []);

  return {
    students,
    loading,
    selectedStudents,
    query,

    isStudentSelected,
    onToggleStudent,
    onToggleAll,
    onSearchStudent,
    onConfirmSelection,
    submitting,
    submitButtonTitle,

    allStudentSelected,
    noStudentsSelected,
    disabled: (noStudentsSelected && !allowEmptySelection) || disabled,
  };
}
